/**
 * TypeScript utilities for working with localStorage, sessionStorage, and cookies
 */
import { trackError } from './error-tracking';

// Type definitions
export type StorageValue = string | number | boolean | object | null;
export type StorageKey = string;

export interface StorageArray<T = unknown> {
  [key: string]: T[];
}

/**
 * Append to an existing array in localStorage or create a new array
 */
export const AddToArray = <T = unknown>(
  key: StorageKey,
  arrayName: string,
  item: T,
  unique: boolean = true
): void => {
  const stored = localStorage[key] ? JSON.parse(localStorage[key]) : {};
  const arr: StorageArray<T> = stored;
  
  if (!arr[arrayName]) {
    arr[arrayName] = [];
  }
  
  const isUnique = arr[arrayName].find((i) => i === item);
  
  if (!unique || typeof isUnique === "undefined") {
    arr[arrayName].push(item);
  }
  
  localStorage[key] = JSON.stringify(arr);
};

/**
 * Remove an item from an array in localStorage by id property
 * Works with arrays of objects that have an 'id' property
 */
export const RemoveFromArray = <T extends { id: string | number }>(
  key: StorageKey,
  arrayName: string,
  id: string | number
): boolean => {
  const stored = localStorage[key] ? JSON.parse(localStorage[key]) : {};
  const arr: StorageArray<T> = stored;
  
  if (!arr[arrayName]) {
    return false;
  }
  
  arr[arrayName] = arr[arrayName].filter((i) => i.id !== id);
  localStorage[key] = JSON.stringify(arr);
  return true;
};

/**
 * Get value from localStorage with type safety
 */
export const LS_Get = <T = unknown>(key: StorageKey): T | null => {
  if (typeof localStorage === "undefined") return null;

  const value = localStorage[key];

  if (!value || value === "undefined" || value === "null" || value === "") return null;

  // Special handling for auth token - return as plain string but validate it
  if (key === 'userAuthToken') {
    // Empty and placeholder tokens were refused above; the token is stored
    // normalized, so it is returned as-is.
    return value as T;
  }

  try {
    return JSON.parse(value) as T;
  } catch (e) {
    // JSON.parse only ever throws a SyntaxError.
    trackError(e as SyntaxError);
    return null;
  }
};

/**
 * Set value in localStorage with type safety
 */
export const LS_Set = <T = unknown>(key: StorageKey, data: T): void => {
  // Special handling for auth token - store as plain string
  if (key === 'userAuthToken' && typeof data === 'string') {
    localStorage[key] = data;
  } else {
    localStorage[key] = JSON.stringify(data);
  }
};

/**
 * Delete value from localStorage
 */
export const LS_Delete = (key: StorageKey): void => {
  localStorage.removeItem(key);
};

/**
 * Set value in sessionStorage
 */
export const SS_Set = <T = unknown>(key: StorageKey, data: T): void => {
  sessionStorage[key] = JSON.stringify(data);
};

/**
 * Get value from sessionStorage with type safety
 */
export const SS_Get = <T = unknown>(key: StorageKey): T | null => {
  const value = sessionStorage[key];
  
  if (!value) return null;
  
  try {
    return JSON.parse(value) as T;
  } catch (e) {
    // JSON.parse only ever throws a SyntaxError.
    trackError(e as SyntaxError);
    return null;
  }
};

/**
 * Delete value from sessionStorage
 */
export const SS_Delete = (key: StorageKey): void => {
  sessionStorage.removeItem(key);
};

/**
 * Clear a cookie by name
 */
export const ClearCookie = (label: string): void => {
  document.cookie = `${label}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
};

/**
 * Cookie management class with type safety
 */
export class Cookies {
  /**
   * Set a cookie with optional expiration and path
   */
  static set(
    name: string,
    value: string,
    days?: number,
    path: string = "/"
  ): void {
    let expires = "";
    
    if (days) {
      const date = new Date();
      date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
      expires = "; expires=" + date.toUTCString();
    }
    
    document.cookie = `${name}=${value}${expires}; path=${path}`;
  }
  
  /**
   * Get a cookie value by name
   */
  static get(name: string): string | null {
    const nameEQ = name + "=";
    const ca = document.cookie.split(";");
    
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === " ") {
        c = c.substring(1, c.length);
      }
      if (c.indexOf(nameEQ) === 0) {
        return c.substring(nameEQ.length, c.length);
      }
    }
    
    return null;
  }
  
  /**
   * Delete a cookie by name
   */
  static delete(name: string): void {
    this.set(name, "", -1);
  }
}

// Export type guards for checking storage availability
export const isLocalStorageAvailable = (): boolean => {
  try {
    const test = '__localStorage_test__';
    localStorage.setItem(test, test);
    localStorage.removeItem(test);
    return true;
  } catch (_e) {
    return false;
  }
};

export const isSessionStorageAvailable = (): boolean => {
  try {
    const test = '__sessionStorage_test__';
    sessionStorage.setItem(test, test);
    sessionStorage.removeItem(test);
    return true;
  } catch (_e) {
    return false;
  }
};